import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { PlusIcon } from "lucide-react";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/lib/supabase";

import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAuth } from "@/context/AuthProvider";

const schema = z
  .object({
    name: z.string({ required_error: "El nombre es obligatorio" }).min(1, {
      message: "Debe tener al menos 1 caracter",
    }),
    business_id: z.string(),
    currency: z.string().min(1),
    initial_balance: z.coerce.number().optional(),
    billing_day: z.coerce.number().optional().nullable(),
    due_day: z.coerce.number().optional().nullable(),
    credit_limit: z.coerce.number().optional().nullable(),
    account_type_id: z.coerce.string(),
  })
  .superRefine((values, ctx) => {
    const isCredito =
      values.account_type_id === "e46db8d3-b116-4a7c-bd2c-3140605eaad1";

    if (isCredito) {
      if (!values.billing_day) {
        ctx.addIssue({
          path: ["billing_day"],
          code: "custom",
          message:
            "El día de facturación es obligatorio para cuentas de crédito.",
        });
      }
      if (!values.due_day) {
        ctx.addIssue({
          path: ["due_day"],
          code: "custom",
          message:
            "El día de vencimiento es obligatorio para cuentas de crédito.",
        });
      }
      if (!values.credit_limit || values.credit_limit < 100) {
        ctx.addIssue({
          path: ["credit_limit"],
          code: "custom",
          message: "El límite de crédito debe ser mayor a 100.",
        });
      }
    }
  });

const CreateAccount = ({
  open,
  setOpen,
  editAccount,
  fetchAccounts,
  limpiar,
}: any) => {
  const [currencies, setCurrencies] = useState([]);
  const [accountTypes, setAccountTypes] = useState([]);
  const [businesses, setBusinesses] = useState("");
  const { user } = useAuth();

  useEffect(() => {
    fetchBussiness();
  }, []);

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      currency: "PEN",
      initial_balance: 0,
      billing_day: null,
      due_day: null,
      credit_limit: null,
      business_id: businesses,
      account_type_id: "",
    },
  });
  useEffect(() => {
    if (editAccount) {
      form.reset({
        ...editAccount,
        business_id: businesses,
      });
    } else {
      form.reset({
        name: "",
        currency: "PEN",
        initial_balance: 0,
        billing_day: null,
        due_day: null,
        credit_limit: null,
        business_id: businesses,
        account_type_id: "",
      });
    }
  }, [editAccount, businesses]);

  useEffect(() => {
    const fetchData = async () => {
      const { data: currencyData }: any = await supabase
        .from("currencies")
        .select("*");
      const { data: accountTypeData }: any = await supabase
        .from("account_types")
        .select("*");
      setCurrencies(currencyData || []);
      setAccountTypes(accountTypeData || []);
    };
    fetchData();
  }, []);
  async function fetchBussiness() {
    const { data, error } = await supabase
      .from("businesses")
      .select("id")
      .eq("user_id", user?.id)
      .single();

    if (!data) {
      // No hay negocio, crearlo
      const { data: newBiz, error: createError } = await supabase
        .from("businesses")
        .insert({
          user_id: user?.id,
          name: "Negocio Nuevo " + user?.id, // Puedes personalizar o dejar campo vacío
          created_at: new Date().toISOString(),
        })
        .select()
        .single();

      if (createError) {
        console.error("Error creando negocio:", createError.message);
        return;
      }

      setBusinesses(newBiz.id);
      return;
    }

    // Ya existe
    setBusinesses(data.id);
  }

  async function onSubmit(values: any) {
    if (editAccount) {
      await supabase.from("accounts").update(values).eq("id", editAccount.id);
    } else {
      await supabase.from("accounts").insert(values);
    }
    setOpen(false);
    limpiar();
    fetchAccounts();
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <PlusIcon className="w-4 h-4 mr-2" />{" "}
          {editAccount ? "Editar" : "Nueva"} Cuenta
        </Button>
      </DialogTrigger>
      <DialogContent>
        <>
          <DialogTitle>
            {editAccount ? "Editar Cuenta" : "Nueva Cuenta"}
          </DialogTitle>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                name="name"
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nombre</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                name="currency"
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Moneda</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl className="w-full">
                        <SelectTrigger>
                          <SelectValue placeholder="Selecciona una moneda" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {currencies.map((c: any) => (
                          <SelectItem key={c.code} value={c.code}>
                            {c.code} - {c.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                name="account_type_id"
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tipo de cuenta</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl className="w-full">
                        <SelectTrigger>
                          <SelectValue placeholder="Selecciona un tipo" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {accountTypes.map((t: any) => (
                          <SelectItem key={t.id} value={t.id}>
                            {t.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                name="initial_balance"
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Saldo inicial</FormLabel>
                    <FormControl>
                      <Input type="number" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {form.watch("account_type_id") ===
                "e46db8d3-b116-4a7c-bd2c-3140605eaad1" && (
                <>
                  <FormField
                    name="billing_day"
                    control={form.control}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Día de facturación</FormLabel>
                        <FormControl>
                          <Input type="number" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    name="due_day"
                    control={form.control}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Día de vencimiento</FormLabel>
                        <FormControl>
                          <Input type="number" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    name="credit_limit"
                    control={form.control}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Límite de crédito</FormLabel>
                        <FormControl>
                          <Input type="number" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </>
              )}
              <Button type="submit" className="w-full">
                Guardar
              </Button>
            </form>
          </Form>
        </>
      </DialogContent>
    </Dialog>
  );
};

export default CreateAccount;
